import React from 'react';
import { CheckCircle, CalendarPlus, RotateCcw, Phone, MapPin } from 'lucide-react';
import { Button, Card, CardContent } from '@clinic-os/ui';

export interface BookingSuccessViewProps {
  appointmentType: string;
  appointmentDate: Date;
  appointmentTime: string;
  patientName: string;
  clinicName?: string;
  clinicPhone?: string;
  clinicAddress?: string;
  onBookAnother: () => void;
  className?: string;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(time: string): string {
  const parts = time.split(':').map(Number);
  const hours = parts[0]!;
  const minutes = parts[1]!;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function generateICSFile(
  appointmentType: string,
  date: Date,
  time: string,
  clinicName: string,
  clinicAddress: string
): string {
  const parts = time.split(':').map(Number);
  const hours = parts[0]!;
  const minutes = parts[1]!;
  const startDate = new Date(date);
  startDate.setHours(hours, minutes, 0, 0);

  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + 30);

  const formatICSDate = (d: Date): string => {
    return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MedFlow//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${Date.now()}@clinic-os`,
    `DTSTAMP:${formatICSDate(new Date())}`,
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${appointmentType} at ${clinicName}`,
    `DESCRIPTION:Your ${appointmentType} appointment at ${clinicName}.`,
    `LOCATION:${clinicAddress}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return icsContent;
}

function downloadICSFile(
  appointmentType: string,
  date: Date,
  time: string,
  clinicName: string,
  clinicAddress: string
): void {
  const icsContent = generateICSFile(appointmentType, date, time, clinicName, clinicAddress);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `appointment-${date.toISOString().split('T')[0]}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function BookingSuccessView({
  appointmentType,
  appointmentDate,
  appointmentTime,
  patientName,
  clinicName = 'Smile Dental Clinic',
  clinicPhone = '+66 81 234 5678',
  clinicAddress = '123 Medical Center Rd, Bangkok',
  onBookAnother,
  className,
}: BookingSuccessViewProps) {
  const handleAddToCalendar = () => {
    downloadICSFile(appointmentType, appointmentDate, appointmentTime, clinicName, clinicAddress);
  };

  return (
    <div className={className}>
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-700" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Appointment Confirmed!</h2>
            <p className="text-slate-500 mb-6">
              Your appointment has been successfully booked.
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-500">Appointment Type</span>
              <span className="font-medium text-slate-900">{appointmentType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Date</span>
              <span className="font-medium text-slate-900">{formatDate(appointmentDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Time</span>
              <span className="font-medium text-slate-900">{formatTime(appointmentTime)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Patient Name</span>
              <span className="font-medium text-slate-900">{patientName}</span>
            </div>
          </div>

          <div className="space-y-3">
            <Button onClick={handleAddToCalendar} className="w-full">
              <CalendarPlus className="w-4 h-4" />
              Add to Calendar
            </Button>
            <Button variant="outline" onClick={onBookAnother} className="w-full">
              <RotateCcw className="w-4 h-4" />
              Book Another Appointment
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-200">
            <h3 className="font-medium text-slate-900 mb-3">Clinic Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="w-4 h-4" />
                <span>{clinicAddress}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4" />
                <span>{clinicPhone}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
